import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Flex, Text, Button, Badge, Spinner, Center, HStack, Input, InputGroup,
  InputLeftElement, Select, Table, Thead, Tbody, Tr, Th, Td, Tooltip, useToast,
  Menu, MenuButton, MenuList, MenuItem,
} from "@chakra-ui/react";
import { Search, RefreshCw, History, ChevronLeft, ChevronRight, Download, ChevronDown } from "lucide-react";
import { getCrmAtividadesLog, exportarCrmAtividades } from "../../services/api";
import { useDebounce } from "../../hooks/useDebounce";
import { formatPhone, formatDateTime, rotuloResultado, corResultado } from "./crmUtils";

const ACCENT = "#F68633";
const PER_PAGE = 50;

const TIPO_INFO = {
  ligacao: { label: "Ligação", cor: "purple" },
  whatsapp: { label: "WhatsApp", cor: "green" },
  tarefa: { label: "Tarefa concluída", cor: "blue" },
};

// Data local yyyy-mm-dd (toISOString viraria o dia em UTC à noite).
const isoLocal = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmtDuracao = (s) => {
  if (!s || s <= 0) return null;
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m${String(s % 60).padStart(2, "0")}s` : `${s}s`;
};

// Log cronológico de atividades do time comercial: registros manuais,
// ligações do discador, mensagens do chat do CRM e tarefas concluídas.
export default function AtividadesTab({ onAbrirFicha, refreshKey }) {
  const hoje = new Date();
  const seteDiasAtras = new Date(hoje.getTime() - 6 * 24 * 60 * 60 * 1000);
  const [inicio, setInicio] = useState(isoLocal(seteDiasAtras));
  const [fim, setFim] = useState(isoLocal(hoje));
  const [vendedor, setVendedor] = useState("");
  const [tipo, setTipo] = useState("");
  const [busca, setBusca] = useState("");
  const [page, setPage] = useState(1);
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportando, setExportando] = useState(false);
  const buscaDeb = useDebounce(busca, 350);
  const toast = useToast();

  const carregar = useCallback(async () => {
    setLoading(true);
    const r = await getCrmAtividadesLog({
      inicio: inicio || undefined,
      fim: fim || undefined,
      vendedor_id: vendedor || undefined,
      tipo: tipo || undefined,
      busca: buscaDeb || undefined,
      page,
      per_page: PER_PAGE,
    });
    setResp(r || null);
    setLoading(false);
  }, [inicio, fim, vendedor, tipo, buscaDeb, page]);

  // Exporta o feed inteiro do período (não só a página atual).
  const exportar = async (formato) => {
    setExportando(true);
    const blob = await exportarCrmAtividades({
      inicio: inicio || undefined,
      fim: fim || undefined,
      vendedor_id: vendedor || undefined,
      tipo: tipo || undefined,
      busca: buscaDeb || undefined,
      formato,
    });
    setExportando(false);

    if (!blob) {
      toast({ title: "Erro ao exportar as atividades", status: "error", duration: 3000 });
      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `crm-atividades-${inicio}-a-${fim}.${formato === "csv" ? "csv" : "xlsx"}`;
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => { carregar(); }, [carregar, refreshKey]);
  // Volta à primeira página quando muda qualquer filtro.
  useEffect(() => { setPage(1); }, [inicio, fim, vendedor, tipo, buscaDeb]);

  const linhas = resp?.data || [];
  const pag = resp?.pagination;
  const isAdmin = !!resp?.is_admin;

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3} gap={2} flexWrap="wrap">
        <HStack spacing={2} flex="1" flexWrap="wrap">
          <HStack spacing={1}>
            <Input size="sm" type="date" maxW="150px" bg="white" value={inicio} onChange={(e) => setInicio(e.target.value)} />
            <Text fontSize="xs" color="gray.400">até</Text>
            <Input size="sm" type="date" maxW="150px" bg="white" value={fim} onChange={(e) => setFim(e.target.value)} />
          </HStack>
          {isAdmin && (
            <Select size="sm" maxW="200px" bg="white" placeholder="Todos vendedores" value={vendedor} onChange={(e) => setVendedor(e.target.value)}>
              {(resp?.vendedores || []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </Select>
          )}
          <Select size="sm" maxW="170px" bg="white" placeholder="Todos os tipos" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="ligacao">Ligação</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="tarefa">Tarefa concluída</option>
          </Select>
          <InputGroup size="sm" maxW="240px">
            <InputLeftElement pointerEvents="none"><Search size={15} color="#999" /></InputLeftElement>
            <Input placeholder="Buscar por cliente..." value={busca} onChange={(e) => setBusca(e.target.value)} bg="white" />
          </InputGroup>
        </HStack>
        <HStack spacing={2}>
          <Menu>
            <MenuButton
              as={Button} size="sm" variant="outline" colorScheme="green"
              leftIcon={<Download size={15} />} rightIcon={<ChevronDown size={14} />}
              isLoading={exportando} loadingText="Gerando" isDisabled={linhas.length === 0}
            >
              Exportar
            </MenuButton>
            <MenuList minW="180px">
              <MenuItem fontSize="sm" onClick={() => exportar("xlsx")}>Excel (.xlsx)</MenuItem>
              <MenuItem fontSize="sm" onClick={() => exportar("csv")}>CSV (.csv)</MenuItem>
            </MenuList>
          </Menu>
          <Button size="sm" variant="ghost" leftIcon={<RefreshCw size={15} />} onClick={carregar}>Atualizar</Button>
        </HStack>
      </Flex>

      {loading ? (
        <Center py={16}><Spinner color={ACCENT} /></Center>
      ) : linhas.length === 0 ? (
        <Center py={16} flexDirection="column" color="gray.400">
          <History size={40} />
          <Text mt={3} fontSize="sm">Nenhuma atividade no período.</Text>
        </Center>
      ) : (
        <>
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Data</Th>
                  <Th>Vendedor</Th>
                  <Th>Cliente</Th>
                  <Th>Tipo</Th>
                  <Th>Resultado</Th>
                  <Th>Observação</Th>
                  <Th>Origem</Th>
                </Tr>
              </Thead>
              <Tbody>
                {linhas.map((a) => {
                  const t = TIPO_INFO[a.tipo] || { label: a.tipo, cor: "gray" };
                  const nome = a.cliente_nome || a.contato_nome;
                  const dur = fmtDuracao(a.duracao_seg);
                  return (
                    <Tr key={a.id}>
                      <Td whiteSpace="nowrap"><Text fontSize="xs" color="gray.600">{formatDateTime(a.data)}</Text></Td>
                      <Td><Text fontSize="xs" noOfLines={1}>{a.vendedor_nome || "—"}</Text></Td>
                      <Td maxW="260px">
                        {a.cliente_id ? (
                          <Text
                            fontSize="sm" fontWeight="semibold" noOfLines={1} cursor="pointer"
                            _hover={{ color: ACCENT, textDecoration: "underline" }}
                            onClick={() => onAbrirFicha(a.cliente_id)}
                          >
                            {nome || `Cliente #${a.cliente_id}`}
                          </Text>
                        ) : (
                          <Text fontSize="sm" noOfLines={1}>{nome || formatPhone(a.telefone) || "—"}</Text>
                        )}
                        {a.telefone && <Text fontSize="xs" color="gray.400" noOfLines={1}>{formatPhone(a.telefone)}</Text>}
                      </Td>
                      <Td><Badge fontSize="10px" colorScheme={t.cor}>{t.label}</Badge></Td>
                      <Td whiteSpace="nowrap">
                        {a.resultado ? (
                          <HStack spacing={1}>
                            <Badge fontSize="10px" colorScheme={corResultado(a.resultado)}>{rotuloResultado(a.resultado)}</Badge>
                            {dur && <Text fontSize="xs" color="gray.400">{dur}</Text>}
                          </HStack>
                        ) : (
                          <Text fontSize="xs" color="gray.300">—</Text>
                        )}
                      </Td>
                      <Td maxW="380px">
                        {a.tarefa_motivo && (
                          <Text fontSize="xs" color="blue.600" noOfLines={1}>Concluiu tarefa: {a.tarefa_motivo}</Text>
                        )}
                        {a.observacao ? (
                          <Tooltip label={a.observacao} openDelay={400} hasArrow placement="top">
                            <Text fontSize="xs" color="gray.600" noOfLines={2}>{a.observacao}</Text>
                          </Tooltip>
                        ) : (
                          !a.tarefa_motivo && <Text fontSize="xs" color="gray.300">—</Text>
                        )}
                      </Td>
                      <Td>
                        <Tooltip label={a.origem_detalhe} openDelay={400} hasArrow>
                          <Badge fontSize="10px" colorScheme={a.origem === "manual" ? "gray" : "cyan"} variant="subtle">
                            {a.origem === "manual" ? "Manual" : "Automático"}
                          </Badge>
                        </Tooltip>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          {pag && (
            <Flex justify="space-between" align="center" mt={3} flexWrap="wrap" gap={2}>
              <Text fontSize="xs" color="gray.500">
                {pag.total.toLocaleString("pt-BR")} atividades · página {pag.page} de {pag.last_page}
              </Text>
              <HStack>
                <Button size="sm" variant="outline" leftIcon={<ChevronLeft size={16} />} isDisabled={pag.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</Button>
                <Button size="sm" variant="outline" rightIcon={<ChevronRight size={16} />} isDisabled={pag.page >= pag.last_page} onClick={() => setPage((p) => p + 1)}>Próxima</Button>
              </HStack>
            </Flex>
          )}
        </>
      )}
    </Box>
  );
}
